import React from "react"
import { PageDrawerAction } from "src/components/ui/PageDrawerAction"
import { Icons } from "src/components/ui/Icons"
import { useActions, useAppState, useReaction } from "src/state"
import { 
    BlackoutFragment,
    Blackouts_Insert_Input,
    UpdateBlackoutMutationVariables
} from "src/state/gql/_types"
import { FormDrawer } from "src/components/forms/FormDrawer"
import { Loader } from "@mantine/core"
import { 
    AgencyOperationsAdminTalentBlackoutCreateFragment, 
    AgencyOperationsAdminTalentBlackoutCreateSchema, 
    AgencyOperationsAdminTalentBlackoutUpdateFragment, 
    AgencyOperationsAdminTalentBlackoutUpdateSchema 
} from "src/components/agency/operations/administrator/talent/blackouts/AgencyOperationsAdminTalentBlackoutSchemas"
import { extractCurrentItemData } from "src/utils/forms"

type FormPayloadType = AgencyOperationsAdminTalentBlackoutCreateFragment

let initialState: FormPayloadType = {
    description: "Unavailable",
    start_at: "",
    end_at: ""
};

export const CreateAgencyOperationsAdminTalentBlackoutDrawer: React.FC<{onClose():void}> = ({ onClose }) => {

    const { createItem, updateItem, deleteItem } = useActions().blackouts;
    const { loading, errors } = useAppState().blackouts;
    const { currentItemId: currentUserId } = useAppState().users;
    
    let currentItemId = null;
    let currentItemData: FormPayloadType | null = null;

    const handleCreate = (payload: Blackouts_Insert_Input) => {
        return createItem({ talent_id: currentUserId, ...payload })
    }

    return (
        <>
            <FormDrawer<FormPayloadType, BlackoutFragment, string>
                autoOpen={false}
                formSchema={AgencyOperationsAdminTalentBlackoutCreateSchema}
                createAction={handleCreate}
                updateAction={updateItem}
                deleteAction={deleteItem}
                isLoading={loading}
                errors={errors}
                currentItemId={currentItemId}
                initialData={initialState}
                currentItemData={currentItemData}
                onClose={onClose}
                deleteRedirect={true}
            />

        </>
    )

}

export const CreateAgencyOperationsAdminTalentBlackoutAction = () => {
    return (
        <PageDrawerAction<BlackoutFragment>
            title='Add Blackout Dates'
            action={CreateAgencyOperationsAdminTalentBlackoutDrawer}
            icon={Icons.add}
        />
    )
}

let initialStateUpdate: AgencyOperationsAdminTalentBlackoutUpdateFragment = {
    description: "",
    start_at: "",
    end_at: ""
};

export const UpdateAgencyOperationsAdminTalentBlackoutDrawer: React.FC<{onClose():void,deselect:boolean}> = ({ onClose, deselect = true }) => {

    
    const { updateItem, deleteItem, deselectItem } = useActions().blackouts;
    const { currentItem, loading, errors } = useAppState().blackouts;

    if (loading || !currentItem) return <Loader />

    let currentItemId = null;
    let currentItemData: AgencyOperationsAdminTalentBlackoutUpdateFragment | null = null;
    if (currentItem) {
        currentItemId = currentItem.id;
        currentItemData = extractCurrentItemData<AgencyOperationsAdminTalentBlackoutUpdateFragment>(initialStateUpdate, currentItem);
    }

    const handleClose = () => {
        if(deselect) {
            deselectItem()
        }
        onClose()
    }

    const handleUpdate = (payload: UpdateBlackoutMutationVariables) => {
        return updateItem({
            id: payload.id,
            _set: {
                ...payload._set
            }
        })
    }

    return (
        <>
            <FormDrawer<AgencyOperationsAdminTalentBlackoutUpdateFragment, BlackoutFragment, string>
                autoOpen={false}
                formSchema={AgencyOperationsAdminTalentBlackoutUpdateSchema}
                updateAction={handleUpdate}
                deleteAction={deleteItem}
                isLoading={loading}
                errors={errors}
                currentItemId={currentItemId}
                initialData={initialStateUpdate}
                currentItemData={currentItemData}
                onClose={handleClose}
                deleteRedirect={true}
            />

        </>
    )

}

export const UpdateAgencyOperationsAdminTalentBlackoutAction = () => {

    const reaction = useReaction()
    const { deselectItem } = useActions().blackouts;
    const [opened, setOpened] = React.useState(false);

    React.useEffect(() => reaction(
        (state) => state.blackouts.currentItemId,
        (selectedID) => {
            setOpened(!!selectedID)
        }, { immediate: false }
    ))

    const handleClose = () => {
        deselectItem()
    }

    return (
        <PageDrawerAction<BlackoutFragment>
            title='Edit Blackout Dates'
            action={UpdateAgencyOperationsAdminTalentBlackoutDrawer}
            icon={Icons.edit}
            startOpened={opened}
            onClose={handleClose}
            hidden
        />
    )
}

export const EditAgencyOperationsAdminTalentBlackoutAction = () => {
    return (
        <PageDrawerAction<BlackoutFragment>
            title='Edit Blackout Dates'
            action={UpdateAgencyOperationsAdminTalentBlackoutDrawer}
            icon={Icons.edit}
            props={{
                deselect: false
            }}
        />
    )
}